import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback } from 'react';
import { notificationService } from '@shared/components/notifications';
import { roleContextApi } from '../api/roleContextApi';
import { roleContextQueryKeys } from './queryKeys';
import { RoleContextDTO } from '../dtos';
import { roleContextStorage } from '../utils/roleContextStorage';
import { useContextStore } from '../../../stores/contextStore';
import { isStorageVersionCompatible } from '../../../utils/storageVersion';
import { useAuthStore } from '../../authentication/store/authStore';

// Get available role contexts for current user
export const useAvailableRoleContexts = () => {
  return useQuery({
    queryKey: roleContextQueryKeys.available(),
    queryFn: async () => {
      const response = await roleContextApi.getAvailableRoleContexts();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
};

// Get current role context from client-side storage with validation
export const useCurrentRoleContext = () => {
  const [currentContext, setCurrentContext] = useState<RoleContextDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      // Check storage version compatibility first
      const isCompatible = isStorageVersionCompatible();
      if (!isCompatible) {
        setCurrentContext(null);
        setIsLoading(false);
        return;
      }

      // Load current context from storage on mount
      const storedContext = roleContextStorage.getCurrentRoleContext();

      // Validate stored context structure
      if (storedContext && !isValidRoleContext(storedContext)) {
        roleContextStorage.clearCurrentRoleContext();
        setCurrentContext(null);
        setError('Invalid role context data - cleared corrupted storage');
        setIsLoading(false);
        return;
      }

      setCurrentContext(storedContext);

      // If we have a valid stored context, ensure it's synced to the main context store
      if (storedContext) {
        try {
          const contextStore = useContextStore.getState();
          contextStore.setActiveContext({
            tenantId: storedContext.tenantId,
            domainId: storedContext.domainId || null,
            subjectId: storedContext.subjectId || null,
          });
        } catch (syncError) {
          console.error('❌ useCurrentRoleContext: Failed to sync to context store:', syncError);
          setError('Failed to sync role context');
        }
      }

      setIsLoading(false);
    } catch (loadError) {
      console.error('❌ useCurrentRoleContext: Failed to load role context:', loadError);
      setError('Failed to load role context from storage');
      setCurrentContext(null);
      setIsLoading(false);

      // Clear potentially corrupted storage
      try {
        roleContextStorage.clearCurrentRoleContext();
      } catch (clearError) {
        console.error('❌ useCurrentRoleContext: Failed to clear corrupted storage:', clearError);
      }
    }
  }, []);

  return {
    data: currentContext,
    isLoading,
    error,
  };
};

/**
 * Validates that the stored role context still exists in available contexts
 * Clears the stored context if it's no longer valid
 */
export const validateStoredRoleContext = (
  storedContext: RoleContextDTO | null,
  availableContexts: RoleContextDTO[]
): RoleContextDTO | null => {
  if (!storedContext) {
    return null;
  }

  // Check if the stored context still exists in available contexts
  const contextExists = availableContexts.find((ctx) => ctx.contextId === storedContext.contextId);

  if (!contextExists) {
    // Clear the invalid stored context
    roleContextStorage.clearCurrentRoleContext();
    return null;
  }

  // DEFENSIVE: Check if stored context has more specific displayName than API response
  // This prevents overwriting correct data with incorrect generic data from backend
  const storedIsMoreSpecific =
    (storedContext.subjectName && storedContext.displayName === storedContext.subjectName) ||
    (storedContext.domainName && storedContext.displayName === storedContext.domainName);

  const apiIsGeneric = contextExists.displayName === contextExists.tenantName;

  if (storedIsMoreSpecific && apiIsGeneric) {
    // Keep the stored context with correct specific name
    // Don't let bad API data overwrite good cached data
    return storedContext;
  }

  // Context is still valid, check if it needs updating (in case server data changed)
  const needsUpdate =
    contextExists.displayName !== storedContext.displayName ||
    contextExists.tenantName !== storedContext.tenantName ||
    contextExists.domainName !== storedContext.domainName ||
    contextExists.subjectName !== storedContext.subjectName;

  if (needsUpdate) {
    // Update stored context with latest server data
    roleContextStorage.setCurrentRoleContext(contextExists);
    return contextExists;
  }

  return storedContext;
};

/**
 * Validate role context structure
 */
const isValidRoleContext = (context: any): context is RoleContextDTO => {
  if (!context || typeof context !== 'object') return false;

  const required = ['contextId', 'displayName', 'tenantId'];
  const hasRequired = required.every((field) => typeof context[field] === 'string');

  return hasRequired;
};

// Get available contexts with current context information
export const useAvailableRoleContextsWithCurrent = () => {
  const {
    data: availableContexts,
    isLoading: isLoadingAvailable,
    error,
  } = useAvailableRoleContexts();
  const { data: currentContext, isLoading: isLoadingCurrentContext } = useCurrentRoleContext();
  const authStore = useAuthStore();
  const [validatedCurrentContext, setValidatedCurrentContext] = useState<RoleContextDTO | null>(
    null
  );

  // Validate stored context against available contexts and handle auto-selection
  useEffect(() => {
    if (
      !availableContexts?.availableContexts?.length ||
      isLoadingAvailable ||
      isLoadingCurrentContext
    ) {
      return;
    }

    // Step 1: Validate current stored context
    const validContext = validateStoredRoleContext(
      currentContext,
      availableContexts.availableContexts
    );
    setValidatedCurrentContext(validContext);

    // Step 2: Auto-select first context if none is valid AND this is initial authentication
    if (!validContext && authStore.isAuthenticated && authStore._hasInitialized) {
      // Check if there's a stored context that we haven't loaded yet (race condition)
      const storedContext = roleContextStorage.getCurrentRoleContext();
      if (storedContext) {
        return;
      }

      try {
        const firstContext = availableContexts.availableContexts[0];

        // Validate first context before auto-selecting
        if (!isValidRoleContext(firstContext)) {
          console.error(
            '❌ useAvailableRoleContextsWithCurrent: First available context is invalid:',
            firstContext
          );
          return;
        }

        // This will automatically sync to main context store via roleContextStorage
        roleContextStorage.setCurrentRoleContext(firstContext);
        setValidatedCurrentContext(firstContext);
      } catch (autoSelectError) {
        console.error(
          '❌ useAvailableRoleContextsWithCurrent: Failed to auto-select context:',
          autoSelectError
        );
      }
    }
  }, [
    availableContexts,
    currentContext,
    isLoadingAvailable,
    isLoadingCurrentContext,
    authStore.isAuthenticated,
    authStore._hasInitialized,
  ]);

  return {
    availableContexts: availableContexts?.availableContexts || [],
    currentContext: validatedCurrentContext, // Use validated context that has been checked against available contexts
    totalCount: availableContexts?.totalCount || 0,
    isLoading: isLoadingAvailable || isLoadingCurrentContext,
    error,
  };
};

// Switch role context (client-side only) with page reload
export const useSwitchRoleContext = () => {
  const queryClient = useQueryClient();

  const switchRoleContext = useCallback(
    (contextId: string, availableContexts: RoleContextDTO[]) => {
      const targetContext = availableContexts.find((ctx) => ctx.contextId === contextId);

      if (!targetContext) {
        console.error('Invalid context ID:', contextId);
        notificationService.error('Invalid role context selected');
        return;
      }

      try {
        // 1. PRESERVE ESSENTIALS - Extract critical data before nuclear destruction
        const authStore = localStorage.getItem('auth-store');
        const uiStore = localStorage.getItem('ui-storage');
        const storageVersion = localStorage.getItem('lecturo_storage_version');
        const surveyLanguage = localStorage.getItem('survey_language');

        // Extract only theme & language preferences from UI store
        let preservedTheme = 'light';
        let preservedLanguage = 'en';

        if (uiStore) {
          try {
            const parsed = JSON.parse(uiStore);
            preservedTheme = parsed.state?.currentTheme || 'light';
            preservedLanguage = parsed.state?.language || 'en';
          } catch (parseError) {
            // Use defaults on parse error
          }
        }

        // 2. NUCLEAR DESTRUCTION - Obliterate everything
        // Clear ALL localStorage (nuclear option)
        const allLocalKeys = Object.keys(localStorage);
        allLocalKeys.forEach((key) => {
          try {
            localStorage.removeItem(key);
          } catch (error) {
            // Ignore errors during cleanup
          }
        });

        // Clear ALL sessionStorage
        const allSessionKeys = Object.keys(sessionStorage);
        allSessionKeys.forEach((key) => {
          try {
            sessionStorage.removeItem(key);
          } catch (error) {
            // Ignore errors during cleanup
          }
        });

        // Clear ALL React Query cache
        queryClient.clear();

        // Reset PostHog analytics (keep identity, destroy groups/properties)
        try {
          const posthog = (window as any)?.posthog;
          if (posthog) {
            posthog.reset(false); // false = keep distinct_id, clear everything else
          }
        } catch (posthogError) {
          // Ignore PostHog reset failures
        }

        // 3. RESTORE ESSENTIALS - Rebuild minimal required state
        // Restore authentication (CRITICAL - must survive)
        if (authStore) {
          localStorage.setItem('auth-store', authStore);
        } else {
          console.error('🚨 CRITICAL: No auth store found to restore!');
        }

        // Restore storage version (prevent migration loops)
        if (storageVersion) {
          localStorage.setItem('lecturo_storage_version', storageVersion);
        }

        // Restore minimal UI preferences for better UX
        const minimalUIStore = {
          state: {
            currentTheme: preservedTheme,
            language: preservedLanguage,
            // Reset all other UI state to defaults
            sidebarOpen: false,
            mobileDrawerOpen: false,
            notifications: [],
          },
          version: 0,
        };
        localStorage.setItem('ui-storage', JSON.stringify(minimalUIStore));

        // Restore survey language preference if it existed
        if (surveyLanguage) {
          localStorage.setItem('survey_language', surveyLanguage);
        }

        // Set new role context (the whole point of this operation)
        sessionStorage.setItem('lecturo_current_role_context', JSON.stringify(targetContext));

        // CRITICAL: Restore context store from role context data
        // This ensures Reports page and other components get the right tenant/subject context
        try {
          const contextStore = useContextStore.getState();

          contextStore.setActiveContext({
            tenantId: targetContext.tenantId,
            domainId: targetContext.domainId || null,
            subjectId: targetContext.subjectId || null,
          });
        } catch (contextError) {
          console.error(
            '🚨 CRITICAL: Failed to restore context store after nuclear switch:',
            contextError
          );
        }

        notificationService.success(`Switched to ${targetContext.displayName}`);

        // 4. HARD RELOAD - Complete fresh start with new role
        setTimeout(() => {
          window.location.reload();
        }, 300); // Reduced timeout for immediate effect
      } catch (error) {
        console.error('Nuclear role switch failed:', error);
        notificationService.error('Failed to switch roles. Please refresh the page and try again.');

        // Emergency fallback - if something went wrong, at least try to reload
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    },
    [queryClient]
  );

  return { switchRoleContext };
};
